// ============================================================
// JIRA JQL QUERIES
// Optimized queries for performance dashboard
// ============================================================

export const JQL_QUERIES = {
  // ============================================================
  // USER-BASED QUERIES
  // ============================================================
  
  // All tasks assigned to a user (open)
  openTasksByAssignee: (accountId: string) =>
    `assignee = "${accountId}" AND resolution = Unresolved ORDER BY updated DESC`,

  // Completed tasks by user (last N days)
  completedTasksByAssignee: (accountId: string, days: number = 30) =>
    `assignee = "${accountId}" AND resolution IS NOT NULL AND resolved >= -${days}d ORDER BY resolved DESC`,

  // All tasks by user (for complete history)
  allTasksByAssignee: (accountId: string, days: number = 90) =>
    `assignee = "${accountId}" AND (resolution IS NULL OR resolved >= -${days}d) ORDER BY updated DESC`,

  // Overdue tasks by user
  overdueByAssignee: (accountId: string) =>
    `assignee = "${accountId}" AND resolution = Unresolved AND duedate < now() ORDER BY duedate ASC`,

  // Tasks updated by user in last N days
  updatedByAssignee: (accountId: string, days: number = 7) =>
    `assignee = "${accountId}" AND updated >= -${days}d ORDER BY updated DESC`,

  // ============================================================
  // GLOBAL QUERIES
  // ============================================================
  
  // All open tasks
  allOpenTasks: () =>
    `resolution = Unresolved ORDER BY updated DESC`,

  // All recently completed tasks
  allRecentlyCompleted: (days: number = 30) =>
    `resolution IS NOT NULL AND resolved >= -${days}d ORDER BY resolved DESC`,

  // All tasks created recently
  allRecentlyCreated: (days: number = 30) =>
    `created >= -${days}d ORDER BY created DESC`,

  // All overdue tasks
  allOverdue: () =>
    `resolution = Unresolved AND duedate < now() ORDER BY duedate ASC`,

  // High priority open tasks
  highPriorityOpen: () =>
    `resolution = Unresolved AND priority in (Highest, High) ORDER BY priority DESC, updated DESC`,

  // ============================================================
  // PROJECT-BASED QUERIES
  // ============================================================
  
  // Tasks by project
  tasksByProject: (projectKey: string) =>
    `project = "${projectKey}" ORDER BY updated DESC`,

  // Open tasks by project
  openTasksByProject: (projectKey: string) =>
    `project = "${projectKey}" AND resolution = Unresolved ORDER BY updated DESC`,

  // Completed tasks by project (last N days)
  completedTasksByProject: (projectKey: string, days: number = 30) =>
    `project = "${projectKey}" AND resolution IS NOT NULL AND resolved >= -${days}d ORDER BY resolved DESC`,

  // ============================================================
  // STATUS-BASED QUERIES
  // ============================================================
  
  // Tasks by status category
  tasksByStatusCategory: (category: 'new' | 'indeterminate' | 'done') =>
    `statusCategory = "${category}" ORDER BY updated DESC`,

  // Tasks in progress
  inProgress: () =>
    `statusCategory = "In Progress" ORDER BY updated DESC`,

  // ============================================================
  // BLOCKING & DEPENDENCY QUERIES
  // ============================================================
  
  // Blocked tasks (has blockers)
  blockedTasks: () =>
    `resolution = Unresolved AND issueFunction in hasLinks("is blocked by") ORDER BY updated DESC`,

  // Tasks blocking others
  blockingTasks: () =>
    `resolution = Unresolved AND issueFunction in hasLinks("blocks") ORDER BY updated DESC`,

  // Blocked tasks by user
  blockedByAssignee: (accountId: string) =>
    `assignee = "${accountId}" AND resolution = Unresolved AND issueFunction in hasLinks("is blocked by") ORDER BY updated DESC`,

  // ============================================================
  // TIME TRACKING QUERIES
  // ============================================================
  
  // Tasks with time logged
  withTimeLogged: () =>
    `timespent > 0 ORDER BY timespent DESC`,

  // Tasks with time logged by user
  withTimeLoggedByAssignee: (accountId: string) =>
    `assignee = "${accountId}" AND timespent > 0 ORDER BY timespent DESC`,

  // Tasks with remaining estimate
  withRemainingEstimate: () =>
    `remainingEstimate > 0 AND resolution = Unresolved ORDER BY remainingEstimate DESC`,

  // ============================================================
  // SPRINT & AGILE QUERIES (if using Jira Software)
  // ============================================================
  
  // Tasks in active sprint
  inActiveSprint: () =>
    `sprint in openSprints() ORDER BY rank ASC`,

  // Tasks in active sprint by assignee
  inActiveSprintByAssignee: (accountId: string) =>
    `assignee = "${accountId}" AND sprint in openSprints() ORDER BY rank ASC`,
}

// ============================================================
// FIELD DEFINITIONS
// ============================================================

// Basic fields for list views
export const BASIC_FIELDS = [
  'summary',
  'issuetype',
  'status',
  'project',
  'priority',
  'assignee',
  'created',
  'updated',
].join(',')

// Standard fields for issue details
export const ISSUE_FIELDS = [
  'summary',
  'description',
  'issuetype',
  'status',
  'project',
  'priority',
  'assignee',
  'reporter',
  'created',
  'updated',
  'resolutiondate',
  'duedate',
  'issuelinks',
  'parent',
  'labels',
  'components',
].join(',')

// Extended fields including time tracking
export const EXTENDED_FIELDS = [
  ...ISSUE_FIELDS.split(','),
  'timetracking',
  'timespent',
  'timeoriginalestimate',
  'aggregatetimespent',
  'aggregatetimeoriginalestimate',
  'progress',
  'worklog',
].join(',')

// All expandable fields for detailed analysis
export const ALL_FIELDS = '*all'

// ============================================================
// EXPAND OPTIONS
// ============================================================

export const EXPAND_OPTIONS = {
  changelog: 'changelog',
  renderedFields: 'renderedFields',
  names: 'names',
  schema: 'schema',
  transitions: 'transitions',
  operations: 'operations',
  editmeta: 'editmeta',
}

// Common expand combinations
export const EXPAND_PRESETS = {
  basic: [],
  withHistory: ['changelog'],
  withTransitions: ['transitions'],
  full: ['changelog', 'renderedFields', 'names'],
}
